import classNames from 'clsx';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadCounter from 'reactApp/components/DownloadCounter/DownloadCounter';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { AiTooltipForMediaFiles } from 'reactApp/ui/AiTooltipForMediaFiles/AiTooltipForMediaFiles';
import { Checkbox } from 'reactApp/ui/DatalistGalleryItem/components/Checkbox';
import { Download } from 'reactApp/ui/DatalistGalleryItem/components/Download';
import { Favorites } from 'reactApp/ui/DatalistGalleryItem/components/Favorites';
import { Pic } from 'reactApp/ui/DatalistGalleryItem/components/Pic';
import { Publish } from 'reactApp/ui/DatalistGalleryItem/components/Publish';
import type { DataListGalleryItemProps } from 'reactApp/ui/DatalistGalleryItem/DataListGalleryItem.types';
import { Hint, HintPosition } from 'reactApp/ui/Hint/Hint';
import { createGaSender } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import { TrackDownloadCountPromo } from '../SharingTooltips/TrackDownloadCountPromo';
import styles from './DataListGalleryItem.css';

const sendGa = createGaSender('gallery-view');

export const DataListGalleryItem = memo((props: DataListGalleryItemProps) => {
    const {
        name,
        kind,
        onCheckboxClick,
        isSelected,
        isActive,
        isSelecting,
        thumbUrl,
        onDownload,
        id,
        ext,
        isPublic,
        isSeoEnabled,
        onPublish,
        isInFavorites,
        onFavorites,
        storage,
        size,
        favoritesAvailable,
        isPopup,
        isPublicDownloaded,
        weblink,
        showPublicButton,
        showPublishButton,
        showDownloadButton,
        isShowAiTooltip,
    } = props;

    const itemRef = useRef<HTMLDivElement>(null);
    const controlsSettings = STORAGE_CONFIG[storage];
    const showPublish = showPublishButton && storage !== EStorageType.albums;

    const routeId = useSelector(getCurrentRouteId);
    const weblinkUrl = getWeblinkFromPublicId(routeId);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || weblink || ''));
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloadTotal = downloadLimits && downloadLimits.total;

    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const handleDropdown = useCallback(
        (isOpen) => {
            sendGa('download-dropdown');
            setDropdownOpen(isOpen);
        },
        [setDropdownOpen]
    );

    const handlePublish = useCallback(
        (e) => {
            sendGa('publish');
            onPublish(e);
        },
        [onPublish]
    );

    const handleDownload = useCallback(() => {
        sendGa('download');
        onDownload();
    }, [onDownload]);

    const handleFavorites = useCallback(() => {
        sendGa('favorites');
        onFavorites();
    }, [onFavorites]);

    const showDoanloadButton = showDownloadButton && !(storage === EStorageType.public && isPublicDownloaded);
    const blockDownload = storage === EStorageType.public && downloadLeft === 0;

    const controls = useMemo(() => {
        return {
            favorites: (favoritesAvailable || storage === EStorageType.gallery) && (
                <Favorites key={1} isActive={!!isInFavorites} onClick={handleFavorites} />
            ),
            download: showDoanloadButton && !blockDownload && (
                <Download
                    key={2}
                    ext={ext}
                    onClick={handleDownload}
                    onDropdown={handleDropdown}
                    storage={storage}
                    size={size}
                    id={id}
                    name={name}
                />
            ),
            publish: showPublish ? (
                <Publish
                    itemId={id}
                    key={3}
                    isActive={isPublic}
                    onClick={handlePublish}
                    isSeoEnabled={isSeoEnabled}
                    showPublicButton={storage === EStorageType.home && showPublicButton}
                />
            ) : null,
        };
    }, [ext, id, isInFavorites, isPublic, size, favoritesAvailable, name, showDoanloadButton, isSeoEnabled, showPublicButton]);

    // tempexp_17847-start
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [showTooltipCounter, setShowTooltipCounter] = useState(false);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const downloads = downloadTotal - downloadLeft;
    const showDownloadCounter = enabledPublicDownloadLimits && storage === EStorageType.home && !isPaidUser && downloads > 0;
    const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;

    const handleCounterClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltipCounter(true);
    };

    const handleCounterPromoHide = () => {
        setShowTooltipCounter(false);
    };
    // tempexp_17847-end

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.root_selected]: isSelected || isDropdownOpen,
                [styles.root_active]: isActive,
                [styles.root_selecting]: isSelecting,
                [styles.root_dropdownOpen]: isDropdownOpen,
                [styles.root_small]: itemRef?.current && itemRef?.current?.getBoundingClientRect().width < 170,
            })}
            onClick={isPopup ? onCheckboxClick : noop}
            ref={itemRef}
        >
            <div className={styles.content}>
                {isShowAiTooltip && (
                    <div className={styles.aiIconContainer}>
                        <AiTooltipForMediaFiles size={28} />
                    </div>
                )}
                <Hint text={name} delay={250} showDelay={true} positionStrategy={HintPosition.relativeToCursor}>
                    <Pic thumbUrl={thumbUrl} ext={ext} kind={kind} />
                </Hint>
            </div>
            <div className={styles.header}>
                {showDownloadCounter && (
                    <div ref={tooltipRef}>
                        <DownloadCounter downloads={downloads} downloadLimit={downloadTotal} onClick={handleCounterClick} isGalleryView />
                    </div>
                )}
                {showTrackDownloadCountPromo && showTooltipCounter && (
                    <TrackDownloadCountPromo
                        targetRef={tooltipRef}
                        downloads={downloads}
                        downloadLimit={downloadTotal}
                        onHide={handleCounterPromoHide}
                    />
                )}
            </div>
            <div className={styles.footer}>
                <Checkbox isActive={isSelected} onClick={onCheckboxClick} />
                {!isPopup && (
                    <div className={styles.rightControls}>
                        {Object.keys(controls).map((control) => controlsSettings[control] && controls[control])}
                    </div>
                )}
            </div>
        </div>
    );
});

DataListGalleryItem.displayName = 'DataListGalleryItem';
